import type { Store } from '@reduxjs/toolkit';
import type { Exception } from '@sentry/core';

import { config } from '../../config';
import messagesEN from '../../features/translations/en-GB.json';
import { reportErrorToSentry } from '../setup/sentry';
import { trace } from '../setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { type DisplayMessages, displayMessagesFetched, localeChanged } from './langSlice';

const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

const translationImports = import.meta.glob<{ default: DisplayMessages }>([
    '../../features/translations/*.json',
    '!../../features/translations/en-GB.json',
]);

export const importDisplayMessages = async (locale: string): Promise<DisplayMessages> => {
    if (locale === DEFAULT_LOCALE) {
        return messagesEN as DisplayMessages;
    }

    const importMessages = translationImports[`../../features/translations/${locale}.json`];
    if (!importMessages) {
        throw new Error(`No display messages found for locale "${locale}".`);
    }

    const { default: displayMessages } = await importMessages();
    return displayMessages;
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
